import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { CloudView, RegionView } from '@lemoncloud/codes-goods-api';
import { ListResult } from '@lemoncloud/codes-goods-api/dist/cores/types';
import { Params, createAsyncDelay } from '@lemoncloud/lemon-web-core';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { PaginationType, useCustomMutation } from '@codes/shared';

import {
    createCloud,
    createRegion,
    deleteCloud,
    deleteRegion,
    fetchCloudById,
    fetchClouds,
    fetchRegionById,
    fetchRegions,
    searchClouds,
    updateCloud,
    updateCloudAtAdmin,
    updateRegion,
    updateRegionAtAdmin,
} from '../api';
import { cloudsKeys, regionKeys } from '../consts';
import { CreateCloudDTO, CreateRegionDTO, UpdateCloudDTO, UpdateRegionDTO } from '../types';

export const useClouds = (params: Params) =>
    useQuery<ListResult<CloudView>>({
        queryKey: cloudsKeys.list(params ?? {}),
        queryFn: async () => {
            const result = await fetchClouds(params);
            const list = result.list || [];

            return {
                ...result,
                list: list.sort((prev, cur) => (cur.order || 0) - (prev.order || 0)),
            } as ListResult<CloudView>;
        },
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });

export const useSearchClouds = (params: Params) =>
    useQuery<PaginationType<CloudView[]>>({
        queryKey: cloudsKeys.list(params ?? {}),
        queryFn: async () => {
            const result = await searchClouds(params);
            return { ...result, data: result.list || [] } as PaginationType<CloudView[]>;
        },
        refetchOnWindowFocus: false,
    });

export const useCloud = (cloudId: string) =>
    useQuery<CloudView>({
        queryKey: cloudsKeys.detail(cloudId),
        queryFn: () => fetchCloudById(cloudId),
        refetchOnWindowFocus: false,
        enabled: !!cloudId,
        refetchOnMount: true,
        staleTime: 0,
    });

/**
 * 클라우드 생성 mutation 훅
 *
 * @example
 * ```tsx
 * const CreateContent = () => {
 *   const queryClient = useQueryClient();
 *   const createCloud = useCreateCloud();
 *
 *   const handleSubmit = async (data: CreateCloudDTO) => {
 *     await createCloud.mutateAsync(data, {
 *       onSuccess: (response) => {
 *         toast.success('클라우드가 생성되었습니다.');
 *         queryClient.invalidateQueries(cloudsKeys.lists());
 *       },
 *       onError: (error) => {
 *         toast.error('클라우드 생성에 실패했습니다.');
 *       }
 *     });
 *   };
 * ```
 */
export const useCreateCloud = () => {
    return useCustomMutation((data: CreateCloudDTO) => createCloud(data), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};

/**
 * 클라우드 수정 mutation 훅
 *
 * @example
 * ```tsx
 * const EditCloud = ({ cloudId }: { cloudId: string }) => {
 *   const updateCloud = useUpdateCloud();
 *
 *   const handleSubmit = async (data: UpdateCloudDTO) => {
 *     await updateCloud.mutateAsync(
 *       { contentId, ...data },
 *       {
 *         onSuccess: () => {
 *           toast.success('수정되었습니다.');
 *         },
 *         onError: (error) => {
 *           toast.error('수정에 실패했습니다.');
 *         }
 *       }
 *     );
 *   };
 * ```
 */
export const useUpdateCloud = () => {
    const queryClient = useQueryClient();

    return useCustomMutation((data: UpdateCloudDTO) => updateCloud(data), {
        onSuccess: async (response, variables) => {
            queryClient.setQueryData(cloudsKeys.detail(variables.cloudId), response);
            await createAsyncDelay(500);
            await queryClient.invalidateQueries(cloudsKeys.lists() as never);
        },
    });
};

export const useUpdateCloudAtAdmin = () => {
    const queryClient = useQueryClient();

    return useCustomMutation((data: UpdateCloudDTO) => updateCloudAtAdmin(data), {
        onSuccess: async (response, variables) => {
            queryClient.setQueryData(cloudsKeys.detail(variables.cloudId), response);
            await createAsyncDelay(500);
            await queryClient.invalidateQueries(cloudsKeys.lists() as never);
        },
    });
};

export const useDeleteCloud = () => {
    return useCustomMutation((id: string) => deleteCloud(id), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};

export const useRegions = (params: Params) =>
    useQuery<ListResult<RegionView>>({
        queryKey: regionKeys.list(params ?? {}),
        queryFn: async () => {
            const result = await fetchRegions(params);
            return result;
        },
        refetchOnWindowFocus: false,
        enabled: !!params['cloudId'],
    });

export const useRegion = (regionId: string) =>
    useQuery<RegionView>({
        queryKey: regionKeys.detail(regionId),
        queryFn: () => fetchRegionById(regionId),
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        enabled: !!regionId,
    });

export const useCreateRegion = () => {
    return useCustomMutation((data: CreateRegionDTO) => createRegion(data), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};

export const useUpdateRegion = () => {
    const queryClient = useQueryClient();

    return useCustomMutation((data: UpdateRegionDTO) => updateRegion(data), {
        onSuccess: async (response, variables) => {
            queryClient.setQueryData(regionKeys.detail(variables.regionId), response);
            await createAsyncDelay(500);
            await queryClient.invalidateQueries(cloudsKeys.lists() as never);
        },
    });
};

export const useUpdateRegionAtAdmin = () => {
    const queryClient = useQueryClient();

    return useCustomMutation((data: UpdateRegionDTO) => updateRegionAtAdmin(data), {
        onSuccess: async (response, variables) => {
            queryClient.setQueryData(regionKeys.detail(variables.regionId), response);
            await createAsyncDelay(500);
            await queryClient.invalidateQueries(cloudsKeys.lists() as never);
        },
    });
};

export const useDeleteRegion = () => {
    return useCustomMutation((id: string) => deleteRegion(id), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};
